import * as Ably from 'ably'
import type {
    ChannelStateChange,
    ConnectionStateChange,
    ErrorInfo,
    InboundMessage,
    PaginatedResult,
    RealtimeChannel,
    TokenParams,
} from 'ably'
import * as Y from 'yjs'

import { TranscriptConnectionErrorType } from 'src/models/TranscriptConnectionState'
import { transcriptHttpClient } from 'src/network'
import { debug } from 'src/services/DebugService'
import { getAwsRegion } from 'src/utils/env'
import {
    FATAL_HTTP_STATUS_TO_ERROR,
    TranscriptCrdtTransport,
    getFatalConnectionErrorFromHttpStatus,
} from 'src/network/TranscriptCrdtTransport'
import { mergeUpdatesChunked } from 'src/utils/yjs'

import {
    ABLY_EU_ENDPOINT,
    ABLY_STATE_TO_TRANSPORT_STATUS,
    ABLY_SYNTHETIC_CODE_BY_ERROR_TYPE,
    ABLY_SYNTHETIC_SERVER_ERROR_CODE,
    HISTORY_PAGE_SIZE,
    HISTORY_RETRY_MAX_ATTEMPTS,
    SYNC_RETRY_BASE_DELAY_MS,
    SYNC_RETRY_MAX_DELAY_MS,
} from './constants'
import type { AblyCrdtTransportOptions } from './types'
import { buildCrdtChannelName, coerceToUint8Array, getErrorTypeFromAblyError } from './utils'

type CrdtSyncResult = Awaited<ReturnType<typeof transcriptHttpClient.crdtSync>>

export class AblyCrdtTransport extends TranscriptCrdtTransport {
    private readonly _doc: Y.Doc
    private readonly _jobId: string
    private readonly _languageCode: string
    private readonly _ablyTokenUrl: string
    private readonly _verbose: boolean

    // Stamps what we apply from the server; we publish only what is unstamped.
    // Without it two clients bounce the same edit back and forth forever.
    private readonly _remoteOrigin = Symbol('transcript_remote')

    private _client: Ably.Realtime | null = null
    private _channel: RealtimeChannel | null = null
    private _isDisconnected = false

    // Bumped on every `attached(resumed=false)` -- the sole trigger for a sync
    // cycle. Async work checks the id at every await boundary and bails on
    // mismatch, so back-to-back attaches can never apply stale results.
    private _currentCycleId = 0

    constructor(options: AblyCrdtTransportOptions) {
        super()

        this._doc = options.doc
        this._jobId = options.jobId
        this._languageCode = options.languageCode
        this._ablyTokenUrl = options.ablyTokenUrl
        this._verbose = options.verbose ?? false
    }

    async connect(): Promise<void> {
        if (this._isDisconnected || this._client) return

        debug.log('AblyCrdtTransport: Connecting', { jobId: this._jobId, languageCode: this._languageCode })

        const isEuRegion = getAwsRegion()?.startsWith('eu')

        this._client = new Ably.Realtime({
            authCallback: this._authCallback,
            autoConnect: false,
            closeOnUnload: false,
            ...(isEuRegion ? { endpoint: ABLY_EU_ENDPOINT } : {}),
            logLevel: this._verbose ? 4 : 0,
        })

        this._client.connection.on(this._onConnectionStateChange)

        const channelName = buildCrdtChannelName(this._jobId, this._languageCode)
        this._channel = this._client.channels.get(channelName)
        this._channel.subscribe(this._onMessage)
        this._channel.on('failed', this._onChannelFailed)
        this._channel.on('suspended', this._onChannelSuspended)
        this._channel.on('attached', this._onChannelAttached)

        // Not in the constructor: the cache loads a saved doc first, unstamped, and an
        // earlier listener would republish all of it.
        this._doc.on('update', this._onDocUpdate)

        debug.log('AblyCrdtTransport: Subscribing to channel', channelName)

        this._client.connect()

        // Sync runs when the channel attaches with `resumed=false`, which is
        // the same signal for both initial attach and post-suspended reattach.
        // Live messages start arriving as soon as the channel attaches and are
        // applied directly: Y.js updates are idempotent, so any overlap with
        // the snapshot delta or with history replay is a no-op.
    }

    disconnect(): void {
        this._isDisconnected = true
        this._tearDown()
        super.destroy()
    }

    /**
     * Unified sync cycle: snapshot then history.
     *
     * `POST /sync` returns a server snapshot whose `lastTimestamp` may lag
     * real ASR by up to ~1 minute, so the snapshot alone does not guarantee
     * the doc is current. We then call `channel.history({ untilAttach: true })`
     * to fetch every message after `lastTimestamp` and apply it; together the
     * two cover any gap up to Ably's retention window.
     *
     * Both phases run sequentially so a network blip on history does not
     * collide with an in-flight snapshot. Stale results from preempted
     * cycles are discarded at every await boundary.
     */
    private _runSync = async (cycleId: number): Promise<void> => {
        const response = await this._runSyncWithRetry()
        if (response === null || this._isDisconnected || cycleId !== this._currentCycleId) return

        try {
            Y.applyUpdate(this._doc, response.delta, this._remoteOrigin)
        } catch {
            // Possibly malformed snapshot delta
            this._emitFatal('server_error')

            return
        }

        try {
            await this._runHistoryReplayWithRetry(response.lastTimestamp, cycleId)
        } catch {
            // History exhausted retries: the doc would be silently incomplete
            // if we proceeded, so escalate to fatal.
            this._emitFatal('server_error')

            return
        }

        if (this._isDisconnected || cycleId !== this._currentCycleId) return

        this.emit('sync', [true])
    }

    private _runSyncWithRetry = async (): Promise<CrdtSyncResult | null> => {
        let attempt = 0

        while (!this._isDisconnected) {
            try {
                const stateVector = Y.encodeStateVector(this._doc)

                return await transcriptHttpClient.crdtSync(this._jobId, this._languageCode, stateVector)
            } catch (error) {
                if (this._isDisconnected) return null

                const fatalError = getFatalConnectionErrorFromHttpStatus(error)
                if (fatalError) {
                    this._emitFatal(fatalError)

                    return null
                }

                const delayMs = Math.min(SYNC_RETRY_MAX_DELAY_MS, SYNC_RETRY_BASE_DELAY_MS * Math.pow(2, attempt))
                debug.log('AblyCrdtTransport: Sync failed, retrying', { attempt: attempt + 1, delayMs, error })
                await this._sleep(delayMs)
                attempt++
            }
        }

        return null
    }

    private _runHistoryReplayWithRetry = async (lastTimestamp: number, cycleId: number): Promise<void> => {
        let attempt = 0

        while (true) {
            try {
                await this._runHistoryReplayOnce(lastTimestamp, cycleId)

                return
            } catch (error) {
                if (this._isDisconnected || cycleId !== this._currentCycleId) return

                attempt++
                if (attempt >= HISTORY_RETRY_MAX_ATTEMPTS) {
                    debug.log('AblyCrdtTransport: History replay exhausted retries', { attempts: attempt, error })
                    throw error
                }

                const delayMs = Math.min(SYNC_RETRY_MAX_DELAY_MS, SYNC_RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1))
                debug.log('AblyCrdtTransport: History replay failed, retrying', { attempt, delayMs, error })
                await this._sleep(delayMs)

                if (this._isDisconnected || cycleId !== this._currentCycleId) return
            }
        }
    }

    private _runHistoryReplayOnce = async (lastTimestamp: number, cycleId: number): Promise<void> => {
        const channel = this._channel
        if (!channel || this._isDisconnected || cycleId !== this._currentCycleId) return

        // Walk newest-first from the attach point. Two terminations: cross the
        // snapshot timestamp (gap closed), or run out of pages before reaching
        // it (Ably retention exceeded -- rare; logged for ops).
        const staged: InboundMessage[] = []
        let crossedAnchor = false

        let page: PaginatedResult<InboundMessage> | null = await channel.history({
            untilAttach: true,
            limit: HISTORY_PAGE_SIZE,
        })

        while (page !== null) {
            if (this._isDisconnected || cycleId !== this._currentCycleId) return

            for (const item of page.items) {
                if (item.timestamp <= lastTimestamp) {
                    // Pages are newest-first, so every remaining item in this
                    // page (and every subsequent page) is at-or-before the
                    // snapshot and already covered by the delta.
                    crossedAnchor = true
                    break
                }
                staged.push(item)
            }

            if (crossedAnchor || !page.hasNext()) break

            page = await page.next()
        }

        if (this._isDisconnected || cycleId !== this._currentCycleId) return

        if (!crossedAnchor) {
            debug.log('AblyCrdtTransport: History retention exhausted before reaching the snapshot timestamp', {
                lastTimestamp,
                oldestFetchedTimestamp: staged.length > 0 ? staged[staged.length - 1].timestamp : null,
            })
        }

        // Pages walked newest-first; reverse so the merged input reads forward
        // in time when introspected. Y.js updates are commutative so order
        // does not affect correctness.
        staged.reverse()

        const updates: Uint8Array[] = []
        for (const item of staged) {
            const update = coerceToUint8Array(item.data)
            if (update) updates.push(update)
        }

        if (updates.length > 0) {
            Y.applyUpdate(this._doc, mergeUpdatesChunked(updates), this._remoteOrigin)
        }

        debug.log('AblyCrdtTransport: History replay applied', {
            applied: updates.length,
            fetched: staged.length,
            crossedAnchor,
        })
    }

    private _sleep = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms))

    private _emitFatal = (error: TranscriptConnectionErrorType): void => {
        if (this._isDisconnected) return

        this._isDisconnected = true
        this._tearDown()
        this.emit('fatal', [error])
        super.destroy()
    }

    private _tearDown = (): void => {
        this._doc.off('update', this._onDocUpdate)

        if (!this._client) return

        debug.log('AblyCrdtTransport: Tearing down client')

        if (this._channel) {
            this._channel.unsubscribe(this._onMessage)
            this._channel.off('failed', this._onChannelFailed)
            this._channel.off('suspended', this._onChannelSuspended)
            this._channel.off('attached', this._onChannelAttached)
            this._channel = null
        }

        this._client.connection.off(this._onConnectionStateChange)
        this._client.close()
        this._client = null
    }

    private _authCallback = async (_tokenParams: TokenParams, callback: (err: ErrorInfo | null, token: any) => void): Promise<void> => {
        try {
            const response = await fetch(this._ablyTokenUrl, { credentials: 'include' })

            if (!response.ok) {
                // Same status map the `/sync` path uses, so a 404 -- trax holds no document
                // for this job -- stays `not_found` and remains eligible for the insights
                // fallback instead of collapsing into `server_error`.
                const errorType: TranscriptConnectionErrorType = FATAL_HTTP_STATUS_TO_ERROR[response.status] ?? 'server_error'
                // The SDK may re-emit this ErrorInfo via a 'failed' ConnectionStateChange.
                // Use a code in Ably's 5-digit range so getErrorTypeFromAblyError reaches the
                // same verdict on re-classification.
                const ablyCode = ABLY_SYNTHETIC_CODE_BY_ERROR_TYPE[errorType] ?? ABLY_SYNTHETIC_SERVER_ERROR_CODE
                const error = new Ably.ErrorInfo(`Auth request failed: ${response.status}`, ablyCode, response.status)

                callback(error, null)
                this._emitFatal(errorType)

                return
            }

            const tokenRequest = await response.json()
            callback(null, tokenRequest)
        } catch (e: unknown) {
            callback(e as ErrorInfo, null)
            this._emitFatal('server_error')
        }
    }

    private _onMessage = (message: InboundMessage): void => {
        if (this._isDisconnected) return

        const update = coerceToUint8Array(message.data)
        if (!update) {
            debug.log('AblyCrdtTransport: Skipping message with non-binary data', { serial: message.serial })

            return
        }

        // Apply directly. Y.js updates are idempotent, so applying a message
        // that the snapshot or history replay also covers is a no-op.
        Y.applyUpdate(this._doc, update, this._remoteOrigin)
    }

    // One update event, one message -- never grouped or delayed (LG-2854 relies on it).
    private _onDocUpdate = (update: Uint8Array, origin: unknown): void => {
        const cameFromServer = origin === this._remoteOrigin

        if (this._isDisconnected || cameFromServer) return

        // TODO(LG-2927): the stable clientID must be set before the first local edit --
        // it rides in these bytes and is what names the author.
        // TODO(LG-2860): an undo or redo of a big change is one update, and it can go over Ably's
        // 64 KiB message limit. Split it before publishing.

        // No name, so no envelope. `publish(update)` throws 40013 -- Ably needs an object.
        void this._channel?.publish({ data: update }).catch(this._onPublishFailed)
    }

    // A failed send is dropped and the edit lost for good -- the update fires once and
    // `/sync` only downloads. TODO(LG-2853): retry or surface it before editing is on.
    private _onPublishFailed = (error: unknown): void => {
        debug.log('AblyCrdtTransport: Publish failed', error)
    }

    private _onChannelFailed = (stateChange: ChannelStateChange): void => {
        if (this._isDisconnected) return

        debug.log('AblyCrdtTransport: Channel failed', stateChange.reason)

        const error: TranscriptConnectionErrorType = stateChange.reason ? getErrorTypeFromAblyError(stateChange.reason) : 'server_error'

        this._emitFatal(error)
    }

    private _onChannelSuspended = (stateChange: ChannelStateChange): void => {
        if (this._isDisconnected) return

        debug.log('AblyCrdtTransport: Channel suspended', stateChange.reason)

        // The actual re-sync is driven by the next `attached(resumed=false)`;
        // here we just tell the UI we're reconnecting.
        this.emit('status', ['connecting'])
    }

    private _onChannelAttached = (stateChange: ChannelStateChange): void => {
        if (this._isDisconnected) return

        // Continuity preserved -- Ably already replayed any in-flight messages
        // and our doc is still current, so no resync work is needed.
        if (stateChange.resumed) return

        debug.log('AblyCrdtTransport: Channel attached, starting sync cycle')

        this._currentCycleId += 1

        // Channel-only suspensions don't transition the underlying connection,
        // so `_onConnectionStateChange` would not re-emit `connected` here.
        // Emit it from the channel side so the UI exits 'connecting' the
        // moment we have a working data path; doc-level catch-up is signalled
        // separately via `sync(true)` once the snapshot+history have applied.
        this.emit('status', ['connected'])

        void this._runSync(this._currentCycleId)
    }

    private _onConnectionStateChange = (stateChange: ConnectionStateChange): void => {
        if (this._isDisconnected) return

        const { current, reason } = stateChange

        debug.log('AblyCrdtTransport: Connection state change', { current, previous: stateChange.previous, reason })

        if (current === 'failed') {
            const error: TranscriptConnectionErrorType = reason ? getErrorTypeFromAblyError(reason) : 'server_error'
            this._emitFatal(error)

            return
        }

        if (current === 'suspended') {
            this.emit('status', ['disconnected'])

            return
        }

        const mappedStatus = ABLY_STATE_TO_TRANSPORT_STATUS[current]
        if (!mappedStatus) return

        this.emit('status', [mappedStatus])
    }
}
